import React, { memo, useState } from "react";
import { ADMISSION_COLORS as C } from "../../constants/admissionTheme";
import { Button, Dialog, DialogActions, DialogContent, DialogTitle } from "@mui/material";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import { toast } from "react-toastify";
import html2pdf from "html2pdf.js";
import { calcAge } from "../../utils/ageUtils";
import { getFacilityDisplayName } from "../../utils/buildConsentHtml";

const CARD_STYLES = `
  .smart-card-root{margin:0;font-family:Arial,sans-serif;background:#f1f5f9;display:flex;align-items:center;justify-content:center;padding:24px;box-sizing:border-box}
  .smart-card{width:360px;border-radius:16px;padding:20px;color:#fff;background:linear-gradient(135deg,#0f172a,#1e3a8a);box-sizing:border-box}
  .smart-card .top{display:flex;justify-content:space-between;align-items:flex-start}
  .smart-card .h{font-size:11px;letter-spacing:2px;opacity:.7}
  .smart-card .logo{font-weight:700;letter-spacing:1px}
  .smart-card .name{font-size:18px;font-weight:600;margin-top:18px}
  .smart-card .uhid{font-family:monospace;font-size:13px;margin-top:4px;opacity:.9}
  .smart-card .row{display:flex;gap:24px;margin-top:14px;font-size:11px}
  .smart-card .row div span{display:block;opacity:.65}
  .smart-card .foot{margin-top:18px;font-size:10px;opacity:.7}
`;

function buildSmartCardMarkup(p) {
  const age = calcAge(p.dob);
  return `
    <div class="smart-card">
      <div class="top"><div class="h">HTMATE HEALTH CARD</div><div class="logo">HTMATE</div></div>
      <div class="name">${p.title || ""} ${p.name || "—"}</div>
      <div class="uhid">${p.uhid || "—"}</div>
      <div class="row">
        <div><span>Gender</span>${p.gender || "—"}</div>
        <div><span>Age</span>${age ?? "—"}</div>
        <div><span>Blood</span>${p.bloodGroup || "—"}</div>
      </div>
      <div class="row">
        <div><span>Mobile</span>${p.primaryMobile || "—"}</div>
        <div><span>ABHA</span>${p.abhaNumber || "—"}</div>
      </div>
      <div class="foot">${getFacilityDisplayName()}</div>
    </div>`;
}

function buildSmartCardHtml(p) {
  return `<!doctype html><html><head><meta charset="utf-8"/><title>${p.uhid || "Smart Card"}</title>
    <style>${CARD_STYLES} body{min-height:100vh;margin:0}</style></head>
    <body class="smart-card-root">${buildSmartCardMarkup(p)}</body></html>`;
}

function SmartCardDialog({ open, onClose, patient }) {
  const html = buildSmartCardHtml(patient);
  const [downloading, setDownloading] = useState(false);

  const download = async () => {
    setDownloading(true);
    let wrapper;
    try {
      wrapper = document.createElement("div");
      wrapper.style.position = "fixed";
      wrapper.style.left = "-9999px";
      wrapper.style.top = "0";
      wrapper.style.width = "420px";
      wrapper.style.zIndex = "-1";

      const styleEl = document.createElement("style");
      styleEl.textContent = CARD_STYLES;
      wrapper.appendChild(styleEl);

      const root = document.createElement("div");
      root.className = "smart-card-root";
      root.innerHTML = buildSmartCardMarkup(patient);
      wrapper.appendChild(root);
      document.body.appendChild(wrapper);

      const filename = `${patient.uhid || "smart-card"}.pdf`;
      await html2pdf()
        .set({
          margin: [12, 12, 12, 12],
          filename,
          image: { type: "jpeg", quality: 0.98 },
          html2canvas: { scale: 2, backgroundColor: "#f1f5f9", useCORS: true },
          jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
        })
        .from(root)
        .save();

      toast.success("Smart card PDF downloaded");
    } catch (err) {
      toast.error(err?.message || "Failed to download smart card PDF");
    } finally {
      if (wrapper?.parentNode) wrapper.parentNode.removeChild(wrapper);
      setDownloading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Patient Smart Card</DialogTitle>
      <DialogContent>
        <iframe srcDoc={html} title="smart-card" style={{ height: 360, width: "100%", border: `1px solid ${C.border}`, borderRadius: 8 }} />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
        <Button
          variant="contained"
          startIcon={<CreditCardIcon />}
          onClick={download}
          disabled={downloading}
        >
          {downloading ? "Downloading…" : "Download PDF"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default memo(SmartCardDialog);
